import React, { Component } from 'react';
import { Button } from './ui/button';
import { AlertTriangle, RefreshCw } from 'lucide-react';

/**
 * Error Boundary for catching React rendering errors.
 * Prevents white-screen crashes by displaying a user-friendly fallback UI.
 */
class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { 
      hasError: false, 
      error: null,
      errorInfo: null
    };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    // Log diagnostic information to console (no secrets/personal data)
    console.error('[ErrorBoundary] Caught rendering error:', {
      message: error?.message,
      name: error?.name,
      componentStack: errorInfo?.componentStack?.slice(0, 500) // Truncate for safety
    });
    
    this.setState({ errorInfo });
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
  };

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      // Custom fallback UI from props, or default fallback
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="flex flex-col items-center justify-center p-6 min-h-[200px] bg-gray-50 rounded-lg border border-gray-200">
          <AlertTriangle className="h-12 w-12 text-amber-500 mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            Something went wrong
          </h3>
          <p className="text-sm text-gray-500 text-center mb-4 max-w-sm">
            {this.props.errorMessage || "We encountered an unexpected error. Please try again."}
          </p>
          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={this.handleRetry}
              className="text-sm"
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Retry
            </Button>
            <Button
              onClick={this.handleReload}
              className="text-sm bg-blue-600 text-white hover:bg-blue-700"
            >
              Reload Page
            </Button>
          </div>
          {process.env.NODE_ENV === 'development' && this.state.error && (
            <details className="mt-4 text-xs text-gray-400 max-w-md">
              <summary className="cursor-pointer">Error details (dev only)</summary>
              <pre className="mt-2 p-2 bg-gray-100 rounded overflow-auto max-h-32">
                {this.state.error.toString()}
              </pre>
            </details>
          )}
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
